import { Box, Card, CardContent, Typography, IconButton, Button, Tooltip, useMediaQuery } from "@mui/material";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { Booking as BookingDetail } from "../../models/BookingDetails";
import { ConfirmDangerModalComponent, ErrorModal } from "@soqs/common";
import { useState, forwardRef } from "react";
import { cancelBookingAsync, setCancelBookingSubmissionStarted, setDates, setDisplayBookings, setPage, setSelectedDate, setSelectedTime, setUploadingDocsForCreatedBooking, submitCaptureDetailsFormAsync, updateCreateBookingFormState } from "../../features/BookingsSlice";
import { useDispatch, useSelector } from "react-redux";
import BookingIcon from "./BookingIcon";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PAGES } from "../CreateBooking/CreateBookingWrapper";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { createForm } from "final-form";
import { getFormattedDate, getFormattedTime } from "../../features/Helper";
import { BookingStatus } from "../../models/Enums";
import TaxPayerDetailsDisplay from "./TaxPayerDetailsDisplay";
import { requesterDetailsFormState } from "../../features/CaptureDetailsSlice";
import { CaptchaDetailsForm } from "../../models/CaptureDetailsForm";
import { CANCELLED_DOCUMENTS_NOT_PROVIDED, USER_CANCELLED_BOOKING } from "../../features/Constants";

const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export interface BookingProps {
  booking: BookingDetail;
}

function Booking(props: BookingProps) {
  const dispatch = useDispatch();
  const capturedDetailsForm = useSelector(requesterDetailsFormState);

  const [confirmClearModal, setConfirmClearModal] = useState(false);
  const [openToast, setOpenToast] = useState(false);
  const [countdown, setCountdown] = useState('');
  const [forceCancelBooking, setForceCancelBooking] = useState(false);

  const matches = useMediaQuery("(max-width:600px)");

  let timer: any = null;

  const displayObject = {
    Time: getFormattedTime(props.booking.BookingTime),
    "Booking Type": props.booking.BookingType,
    Province: props.booking.Province,
    Branch: props.booking.BranchName,
    "Reason Category": props.booking.ReasonCategory
  }

  const handleClose = () => {
    setOpenToast(false);
  };

  const getTimeLeftToUploadDocs = () => {
    const deadline = new Date(props.booking.DateCreated).getTime() + 60 * 60 * 24 * 1000;
    let difference = Math.abs(deadline.valueOf() - new Date().valueOf()) / 1000;

    const days = Math.floor(difference / 86400);
    difference -= days * 86400;

    const hours = Math.floor(difference / 3600) % 24;
    difference -= hours * 3600;

    const minutes = Math.floor(difference / 60) % 60;
    difference -= minutes * 60;

    const seconds = Math.floor(difference % 60); 

    if (seconds === 0 && minutes === 0 && hours === 0) { 
      clearInterval(timer);
      timer = null;
      setForceCancelBooking(true);
    } else {
      setCountdown(`${hours}h ${minutes}m ${seconds}s`);
    }
  }

  timer = setInterval(getTimeLeftToUploadDocs, 1000);

  const setBookingDetails = () => {
    const form = createForm({
      onSubmit: () => {}, 
      initialValues: {
        bookingType: displayObject["Booking Type"], 
        branch: displayObject.Branch, 
        region: displayObject.Province,
        reason: props.booking.ReasonDescription, 
        reasonCategory: displayObject["Reason Category"],
        reasonSummary: props.booking.ReasonSummary,
        date: props.booking.DateCreated,
        time: props.booking.DateCreated,
      }
    });
    const state = form.getState();

    dispatch(updateCreateBookingFormState(state));
    dispatch(setSelectedDate(props.booking.BookingDate));
    dispatch(setSelectedTime(props.booking.BookingTime));
  }

  const uploadBookingDocuments = (): void => {
    setBookingDetails();
    dispatch(setDisplayBookings(false));
    dispatch(setUploadingDocsForCreatedBooking(true));
    dispatch(setPage(PAGES.UploadSupportingDocs));
    clearInterval(timer);
  }

  return (
    <>
    <Card
      sx={{
        display: "flex",
        width: 1,
        boxShadow: "none",
        border: "1px solid #EBEBEB",
        borderRadius: "8px",
        mb: "12px"
      }}
    >
      <CardContent
        sx={{
          width: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ width: "fit-content", mb: "12px" }} role="status">
            {props.booking.BookingStatus === BookingStatus.Booked
              ? <Typography sx={{color: "#1d4f1f", backgroundColor: "rgba(221, 243, 240, 1)", p: "2px 8px", fontSize: "14px", borderRadius: "4px" }}>Confirmed</Typography> 
              : null}

              {props.booking.BookingStatus === BookingStatus.AwaitingDocs
              ? <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography sx={{color: "#b25102", backgroundColor: "#fffae8", p: "2px 8px", fontSize: "14px", borderRadius: "4px" }}>Awaiting Supporting Documents</Typography>
                  <Tooltip title="You can upload your documents at a later stage, but you will only have 24 hours to upload the required documents." placement="right">
                    <IconButton><InfoOutlinedIcon color="primary" sx={{ ml: "8px" }} /></IconButton>
                  </Tooltip>
                </Box>
              : null}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            {props.booking.BookingStatus === BookingStatus.AwaitingDocs && !matches ? <Button
              variant="outlined"
              color="primary"
              onClick={uploadBookingDocuments}
              sx={{ mr: "8px" }}
            >
              Upload Documents
            </Button> : null}

            {props.booking.BookingStatus === BookingStatus.AwaitingDocs && matches ? <Box
              sx={(theme) => ({
                backgroundColor: theme.palette.primary.main,
                borderRadius: "50%",
                mr: "8px"
              })}
            >
              <IconButton aria-label="upload documents" onClick={uploadBookingDocuments}>
                <AttachFileOutlinedIcon sx={{ color: "white", fontSize: "1.2rem" }}/>
              </IconButton>
            </Box> : null}

            <Box
              sx={(theme) => ({
                backgroundColor: theme.palette.primary.main,
                borderRadius: "50%",
              })}
            >
              <IconButton aria-label="cancel booking" onClick={() => {setConfirmClearModal(true);}}>
                <DeleteOutlineOutlinedIcon sx={{ color: "white", fontSize: "1.2rem" }}/>
              </IconButton>
            </Box>
          </Box>
        </Box>

        {props.booking.BookingStatus === BookingStatus.AwaitingDocs ? <section style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", marginBottom: "12px" }}>
          <TimerOutlinedIcon sx={(theme) => ({ color: theme.palette.primary.main, fontSize: "20px", mr: "4px" })}></TimerOutlinedIcon>
          <Typography sx={(theme) => ({ color: theme.palette.primary.main, fontSize: "12px" })}>You have <strong>{countdown}</strong> to upload your documents</Typography>
        </section> : null}

        <Box sx={(theme) => ({display: "flex", alignItems: "flex-start", [theme.breakpoints.down('md')]: {flexDirection: "column"}})}>
          {capturedDetailsForm?.values?.entityType && capturedDetailsForm?.values?.entityType !== ""
            ? <TaxPayerDetailsDisplay form={capturedDetailsForm?.values as CaptchaDetailsForm} />
            : null
          }

          <Box sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }} role="timer">
              <p style={{fontSize: "22px", fontWeight: "bold", color: "#00558B", marginTop: "8px", marginBottom: 0}}>{getFormattedDate(props.booking.BookingDate)}</p>
            </Box>

            {props.booking.BookingStatus === BookingStatus.Booked ? <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }} role="timer">
              <p style={{fontSize: "14px", color: "#00558B", marginTop: "8px", marginBottom: 0}}>CASE NO: {props.booking.CaseNo}</p>
            </Box> : null}

            <section style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
              {Object.keys(displayObject).map((key: string) => 
                {return (<Box
                  key={key}
                  sx={(theme) => ({
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: " flex-start",
                    alignItems: "center",
                    flex: "0 0 25%",
                    [theme.breakpoints.down('md')]: {
                      flex: '0 0 50%',
                    },
                    [theme.breakpoints.down('sm')]: {
                      flex: '0 0 100%',
                    },
                    pt: "12px"
                  })}
                >
                  <Box
                    sx={{
                      backgroundColor: "rgba(0, 109, 166, 0.11)",
                      borderRadius: "8px",
                      p: "6px 8px",
                    }}
                  >
                    <BookingIcon type={key} />
                  </Box>
                  <section style={{ paddingLeft: "10px" }}>
                    <Typography sx={{ fontSize: "12px" }}>
                      <strong>{key}:</strong>
                    </Typography>
                    <Typography sx={(theme) => ({ color: "#495058", fontSize: theme.typography.body1 })}>
                    {(displayObject as any)[key]}
                    </Typography>
                  </section>
                </Box>)
              })}

              <Box
                sx={(theme) => ({
                  display: "flex",
                  flexDirection: "column",
                  flex: "0 0 75%",
                  [theme.breakpoints.down('md')]: {
                    flex: '0 0 100%',
                  },
                  pt: "22px"
                })}
              >
                <Typography sx={(theme) => ({ fontSize: theme.typography.h6, pl: "8px" })}>
                  <strong>{props.booking.ReasonDescription}</strong>
                </Typography>
                <Typography sx={(theme) => ({ fontSize: theme.typography.body1, pl: "8px" })}>{props.booking.ReasonSummary}</Typography>
              </Box>
            </section>
          </Box>
        </Box>

        <ConfirmDangerModalComponent
          open={confirmClearModal}
          onClose={() => {
            setConfirmClearModal(false);
          }}
          cbConfirm={async () => {
            setConfirmClearModal(false);
            dispatch(setCancelBookingSubmissionStarted(true));
            const result = await dispatch(cancelBookingAsync({cancelReason: USER_CANCELLED_BOOKING, bookingId: props.booking.BookingID}));
            if((result as any).meta.requestStatus === "fulfilled") {
              setOpenToast(true);
              dispatch(submitCaptureDetailsFormAsync());
              dispatch(setDates([]));
            }
          }}
          title="Cancel Booking"
          subheader="Are you sure you want to cancel your booking?"
          confirmText="Yes, Cancel"
          cancelText="Close"
        />

        <ErrorModal
          title="Cancel Booking"
          open={forceCancelBooking}
          onClose={async () => {
            const result = await dispatch(cancelBookingAsync({cancelReason: CANCELLED_DOCUMENTS_NOT_PROVIDED, bookingId: props.booking.BookingID}));
            if((result as any).meta.requestStatus === "fulfilled") {
              setOpenToast(true);
              dispatch(submitCaptureDetailsFormAsync());
              dispatch(setDates([]));
            }
            setForceCancelBooking(false);
          }}
          subheader={`Your booking, ${getFormattedDate(props.booking.BookingDate)} at ${getFormattedTime(props.booking.BookingTime)} is being cancelled because you have failed to upload the required documents within 24 hours.`}
        />
      </CardContent>
    </Card>

    <Snackbar open={openToast} autoHideDuration={6000} onClose={handleClose} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
      <Alert onClose={handleClose} severity="success" sx={{ width: '100%' }} icon={false}>
        Successfully cancelled booking
      </Alert>
    </Snackbar>
    </>
  );
}

export default Booking;
